/**
 * Dynamic CSV Export Helper
 * Works with any table by reading columns configuration and data dynamically
 */

/**
 * Extracts the actual value from a table row based on column configuration
 * @param {Object} row - Table row data
 * @param {Object} column - Column configuration object
 * @returns {string} - Extracted value as string
 */
const extractValue = (row, column) => {
  try {
    // If column has a render function, we need to extract the raw value
    if (column.render) {
      // For columns with render functions, try to get the raw value from accessor
      if (column.accessor) {
        const value = getNestedValue(row, column.accessor);
        return formatValue(value);
      }
      // If no accessor, try to get from the column header as a key
      const key = column.header?.toLowerCase().replace(/\s+/g, '_');
      if (key && row[key] !== undefined) {
        return formatValue(row[key]);
      }
      return 'N/A';
    }
    
    // For columns without render functions, use accessor or header
    if (column.accessor) {
      const value = getNestedValue(row, column.accessor);
      return formatValue(value);
    }
    
    // Fallback to header as key
    const key = column.header?.toLowerCase().replace(/\s+/g, '_');
    if (key && row[key] !== undefined) {
      return formatValue(row[key]);
    }
    
    return 'N/A';
  } catch (error) {
    console.error(`Error extracting value for column ${column.header}:`, error);
    return 'N/A';
  }
};

/**
 * Gets nested object values using dot notation (e.g., "creator.firstName")
 * @param {Object} obj - Object to search
 * @param {string} path - Dot notation path
 * @returns {any} - Value at path
 */
const getNestedValue = (obj, path) => {
  if (!path) return obj;
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

/**
 * Formats a value for CSV export
 * @param {any} value - Value to format
 * @returns {string} - Formatted string
 */
const formatValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value.toString();
  if (value instanceof Date) {
    return new Intl.DateTimeFormat(navigator.language).format(value);
  }
  if (typeof value === 'object') {
    // Handle objects like {firstName: 'John', lastName: 'Doe'}
    if (value.firstName && value.lastName) {
      return `${value.firstName} ${value.lastName}`.trim();
    }
    // Handle other objects by converting to string
    return JSON.stringify(value);
  }
  return String(value);
};

/**
 * Escapes CSV values to handle commas, quotes, and newlines
 * @param {string} value - Value to escape
 * @returns {string} - Escaped value
 */
const escapeCSVValue = (value) => {
  if (typeof value !== 'string') return value;
  
  // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  
  return value;
};

/**
 * Exports table data to CSV dynamically
 * @param {Array} columns - Table columns configuration
 * @param {Array} data - Table data
 * @param {string} filename - CSV filename (without extension)
 * @param {Object} options - Additional options
 * @param {boolean} options.includeHeaders - Whether to include column headers (default: true)
 * @param {Function} options.valueExtractor - Custom function to extract values (optional)
 * @param {Function} options.valueFormatter - Custom function to format values (optional)
 */
export const exportTableToCSV = (columns, data, filename, options = {}) => {
  try {
    const {
      includeHeaders = true,
      valueExtractor = extractValue,
      valueFormatter = formatValue
    } = options;

    // Extract headers from columns
    const headers = columns.map(col => col.header || col.accessor || 'Unknown');
    
    // Generate CSV rows
    const csvRows = [];
    
    // Add headers if requested
    if (includeHeaders) {
      csvRows.push(headers);
    }
    
    // Add data rows
    data.forEach(row => {
      const rowData = columns.map(column => {
        let value = valueExtractor(row, column);
        value = valueFormatter(value);
        return escapeCSVValue(value);
      });
      csvRows.push(rowData);
    });
    
    // Convert to CSV string
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Cleanup
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error exporting CSV:', error);
    throw new Error('Failed to export CSV');
  }
};

/**
 * Simple CSV export for basic data arrays
 * @param {Array} data - Array of objects
 * @param {string} filename - CSV filename (without extension)
 */
export const exportSimpleCSV = (data, filename) => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }
  
  const headers = Object.keys(data[0]);
  const csvRows = [headers];
  
  data.forEach(row => {
    const rowData = headers.map(header => {
      const value = row[header];
      return escapeCSVValue(formatValue(value));
    });
    csvRows.push(rowData);
  });
  
  const csvContent = csvRows.map(row => row.join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return true;
};
